import { useEffect, useMemo } from 'react';
import { marketDataRuntime } from '../../data/client/marketDataRuntime';
import type { Quote, QuoteField } from '../../data/protocol/types';
import { quoteStore, useQuoteStats, useQuoteUniverse } from '../../data/stores/quoteStore';
import { formatNumber, formatSigned } from '../../shared/format';
import {
  getHeatMapSymbolsForSector,
  HEAT_MAP_SECTORS
} from '../../market/marketGroups';
import type { HeatMapWindowConfig, WindowInstance } from '../../workspace/types';

interface HeatMapWindowProps {
  window: WindowInstance<HeatMapWindowConfig>;
}

type SectorSymbols = {
  id: string;
  name: string;
  symbols: string[];
};

type SectorSnapshot = SectorSymbols & {
  averageChange: number;
  averageChangePct: number;
  activeCount: number;
  leaders: Quote[];
};

const HEAT_MAP_FIELDS: QuoteField[] = [
  'symbol',
  'last',
  'change',
  'changePct',
  'time'
];

export function HeatMapWindow({ window }: HeatMapWindowProps) {
  const universe = useQuoteUniverse();
  const stats = useQuoteStats();
  const maxSymbols = window.config.maxSymbols ?? 72;

  const sectorSymbols = useMemo<SectorSymbols[]>(() => {
    const maxPerSector = Math.max(4, Math.ceil(maxSymbols / HEAT_MAP_SECTORS.length));

    return HEAT_MAP_SECTORS.map((sector) => ({
      id: sector.id,
      name: sector.name,
      symbols: getHeatMapSymbolsForSector(sector.id, universe).slice(0, maxPerSector)
    }));
  }, [universe, maxSymbols]);

  const subscribedSymbols = useMemo(() => {
    return Array.from(
      new Set(sectorSymbols.flatMap((sector) => sector.symbols))
    );
  }, [sectorSymbols]);

  const sectors = useMemo<SectorSnapshot[]>(() => {
    return sectorSymbols.map((sector) => {
      const quotes = sector.symbols.map((symbol) => quoteStore.getQuote(symbol));
      const activeQuotes = quotes.filter((quote) => quote.last > 0);
      const divisor = Math.max(activeQuotes.length, 1);

      const averageChange = activeQuotes.reduce((total, quote) => total + quote.change, 0) / divisor;
      const averageChangePct = activeQuotes.reduce((total, quote) => total + quote.changePct, 0) / divisor;

      const leaders = [...activeQuotes]
        .sort((left, right) => Math.abs(right.changePct) - Math.abs(left.changePct))
        .slice(0, 5);

      return {
        ...sector,
        averageChange,
        averageChangePct,
        activeCount: activeQuotes.length,
        leaders
      };
    });
  }, [sectorSymbols, stats.lastSeq]);

  useEffect(() => {
    if (subscribedSymbols.length === 0) return undefined;

    return marketDataRuntime.subscribeQuotes(
      window.id,
      subscribedSymbols,
      HEAT_MAP_FIELDS
    );
  }, [window.id, subscribedSymbols]);

  const marketAverage = sectors.reduce((total, sector) => {
    return total + sector.averageChangePct;
  }, 0) / Math.max(sectors.length, 1);

  return (
    <div className="heat-map-window no-window-drag">
      <div className="heat-map__summary">
        <div>
          <span className="heat-map__eyebrow">Sector heat map</span>
          <strong>Market breadth</strong>
        </div>

        <span className={marketAverage >= 0 ? 'heat-map__market heat-map__market--positive' : 'heat-map__market heat-map__market--negative'}>
          {formatSigned(marketAverage)}%
        </span>
      </div>

      <div className="heat-map__grid">
        {sectors.map((sector) => (
          <HeatMapSector key={sector.id} sector={sector} />
        ))}
      </div>
    </div>
  );
}

function HeatMapSector({ sector }: { sector: SectorSnapshot }) {
  const tone =
    sector.averageChangePct > 0
      ? 'positive'
      : sector.averageChangePct < 0
        ? 'negative'
        : 'neutral';

  const intensity = intensityForMove(sector.averageChangePct);

  return (
    <section className={`heat-map__tile heat-map__tile--${tone} heat-map__tile--${intensity}`}>
      <header className="heat-map__tile-header">
        <span>{sector.name}</span>
        <strong>{formatSigned(sector.averageChangePct)}%</strong>
      </header>

      <div className="heat-map__tile-change">
        <span>{formatSigned(sector.averageChange)}</span>
        <small>{sector.activeCount} names</small>
      </div>

      <div className="heat-map__names">
        {sector.leaders.length > 0 ? (
          sector.leaders.map((quote) => (
            <span key={quote.symbol} className={quote.changePct >= 0 ? 'heat-map__name heat-map__name--positive' : 'heat-map__name heat-map__name--negative'}>
              <span>{quote.symbol}</span>
              <span>{formatSigned(quote.changePct)}%</span>
            </span>
          ))
        ) : (
          <span className="heat-map__empty">Waiting for quotes</span>
        )}
      </div>
    </section>
  );
}

function intensityForMove(value: number): string {
  const absolute = Math.abs(value);

  if (absolute >= 1.5) return 'high';
  if (absolute >= 0.65) return 'medium';

  return 'low';
}